import React, { memo, useMemo } from "react";
import classNames from "classnames";
import { Row, Col, FormGroup, Label, Input } from "reactstrap";
import Select from "components/Select";
import StatesInput from "components/StatesInput";
import { getActiveColorStyle } from "utils/colorUtils";
import {
  getTheme2FormPageDarkSelectStyles,
} from "utils/theme2MultiSelect";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";

const ChannelDetailForm = memo(({
    formData,
    setFormData,
    handleInputChange,
    handleSingleSelectChange,
    mapValueToOption,
    errors,
    activeColor = "blue",
    isTheme2 = false,
    statusOptions,
    countryOptions,
    bookkeepingCompanyOptions,
    locationOptions,
    isEditMode,
    isStateRequired,
    channelType,
    createChannelMutation,
    updateChannelMutation,
    account,
    t
}) => {
    const safeFormData = formData || {};
    const isSuperAdmin = !!account?.is_super_admin;
    const descriptiveNameDisabled =
        createChannelMutation.isPending ||
        updateChannelMutation.isPending ||
        (isEditMode && !isSuperAdmin);
    const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
    const selectStyles = useMemo(
        () => getTheme2FormPageDarkSelectStyles(activeColorStyle, isTheme2),
        [activeColorStyle, isTheme2]
    );
    const inputStyle = isTheme2 ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } : undefined;
    const inputClass = (...extra) => classNames("form-control", ...extra.filter(Boolean));

    return (
        <div>
            <Row>
                <Col md="4">
                    <FormGroup>
                        <Label for='name' className='form-control-label'>
                            {t('channelsAdd.name')} <span style={{ color: 'red' }}>*</span>
                        </Label>
                        <Input
                            id="name"
                            name="name"
                            type="text"
                            value={safeFormData.name || ''}
                            onChange={handleInputChange}
                            className={inputClass(errors.name && "is-invalid")}
                            style={inputStyle}
                            placeholder={t('channelsAdd.enterChannelName')}
                            disabled={descriptiveNameDisabled}
                        />
                        {errors.name && (
                            <div className="invalid-feedback d-block">
                                {errors.name}
                            </div>
                        )}
                    </FormGroup>
                </Col>
                {/* <Col md="4">
                    <FormGroup>
                        <Label for='description' className='form-control-label'>
                            {t('channelsAdd.description')}
                        </Label>
                        <Input
                            id="description"
                            name="description"
                            type="text"
                            value={safeFormData.description}
                            onChange={handleInputChange}
                            className={errors.description ? 'is-invalid' : ''}
                            placeholder={t('channelsAdd.enterChannelDescription')}
                            disabled={createChannelMutation.isPending || updateChannelMutation.isPending}
                        />
                        {errors.description && (
                            <div className="invalid-feedback d-block">
                                {errors.description}
                            </div>
                        )}
                    </FormGroup>
                </Col> */}
                <Col md="4">
                    <FormGroup>
                        <Label for='status' className='form-control-label'>
                            {t('channelsAdd.status')}
                        </Label>
                        <Select
                            activeColor={activeColor}
                            styles={selectStyles}
                            className="react-select"
                            name="status"
                            isSearchable
                            closeMenuOnSelect
                            options={statusOptions}
                            value={mapValueToOption(safeFormData.status, statusOptions)}
                            onChange={handleSingleSelectChange('status')}
                            isDisabled={createChannelMutation.isPending || updateChannelMutation.isPending}
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col md="6">
                    <FormGroup>
                        <Label for='fed_id' className='form-control-label'>
                            {t('channelsAdd.fedId')}
                        </Label>
                        <Input
                            id="fed_id"
                            name="fed_id"
                            type="text"
                            value={safeFormData.fed_id}
                            onChange={handleInputChange}
                            className="form-control"
                            style={inputStyle}
                            placeholder={t('channelsAdd.enterFedId')}
                            disabled={createChannelMutation.isPending || updateChannelMutation.isPending}
                        />
                    </FormGroup>
                </Col>
                <Col md="6">
                    <FormGroup>
                        <Label for='legal_name' className='form-control-label'>
                            {t('channelsAdd.legalName')} <span style={{ color: 'red' }}>*</span>
                        </Label>
                        <Input
                            id="legal_name"
                            name="legal_name"
                            type="text"
                            value={safeFormData.legal_name}
                            onChange={handleInputChange}
                            className={inputClass(errors.legal_name && "is-invalid")}
                            style={inputStyle}
                            placeholder={t('channelsAdd.enterLegalName')}
                            disabled={createChannelMutation.isPending || updateChannelMutation.isPending}
                        />
                        {errors.legal_name && (
                            <div className="invalid-feedback d-block">
                                {errors.legal_name}
                            </div>
                        )}
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md="6">
                    <FormGroup>
                        <Label for='address_1' className='form-control-label'>
                            {t('channelsAdd.address1')} <span style={{ color: 'red' }}>*</span>
                        </Label>
                        <Input
                            id="address_1"
                            name="address_1"
                            type="text"
                            value={safeFormData.address_1}
                            onChange={handleInputChange}
                            className={inputClass(errors.address_1 && "is-invalid")}
                            style={inputStyle}
                            placeholder={t('channelsAdd.enterAddressLine1')}
                            disabled={createChannelMutation.isPending || updateChannelMutation.isPending}
                        />
                        {errors.address_1 && (
                            <div className="invalid-feedback d-block">
                                {errors.address_1}
                            </div>
                        )}
                    </FormGroup>
                </Col>
                <Col md="6">
                    <FormGroup>
                        <Label for='address_2' className='form-control-label'>
                            {t('channelsAdd.address2')}
                        </Label>
                        <Input
                            id="address_2"
                            name="address_2"
                            type="text"
                            value={safeFormData.address_2}
                            onChange={handleInputChange}
                            className="form-control"
                            style={inputStyle}
                            placeholder={t('channelsAdd.enterAddressLine2')}
                            disabled={createChannelMutation.isPending || updateChannelMutation.isPending}
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col md="4">
                    <FormGroup>
                        <Label for='zip' className='form-control-label'>
                            {t('channelsAdd.zip')} <span style={{ color: 'red' }}>*</span>
                        </Label>
                        <Input
                            id="zip"
                            name="zip"
                            type="text"
                            value={safeFormData.zip}
                            onChange={handleInputChange}
                            className={inputClass(errors.zip && "is-invalid")}
                            style={inputStyle}
                            placeholder={t('channelsAdd.enterZipCode')}
                            disabled={createChannelMutation.isPending || updateChannelMutation.isPending}
                        />
                        {errors.zip && (
                            <div className="invalid-feedback d-block">
                                {errors.zip}
                            </div>
                        )}
                    </FormGroup>
                </Col>
                <Col md="4">
                    <FormGroup>
                        <Label for='city' className='form-control-label'>
                            {t('channelsAdd.city')} <span style={{ color: 'red' }}>*</span>
                        </Label>
                        <Input
                            id="city"
                            name="city"
                            type="text"
                            value={safeFormData.city}
                            onChange={handleInputChange}
                            className={inputClass(errors.city && "is-invalid")}
                            style={inputStyle}
                            disabled={createChannelMutation.isPending || updateChannelMutation.isPending}
                        >
                        </Input>
                        {errors.city && (
                            <div className="invalid-feedback d-block">
                                {errors.city}
                            </div>
                        )}
                    </FormGroup>
                </Col>
                <Col md="4">
                    <FormGroup>
                        <Label for='state' className='form-control-label'>
                            {t('channelsAdd.state')} {isStateRequired && <span style={{ color: 'red' }}>*</span>}
                        </Label>
                        <StatesInput
                            inputId="state"
                            name="state"
                            activeColor={activeColor}
                            styles={selectStyles}
                            className="react-select"
                            isSearchable={true}
                            placeholder={!isStateRequired ? 'No states for selected country' : 'Select state'}
                            value={safeFormData.state?.id ? safeFormData.state.id : null}
                            onChange={(stateObject) => {
                                // stateObject = { id, abbreviation, name } or null
                                const value = stateObject ? {
                                    id: stateObject.id,
                                    abbreviation: stateObject.abbreviation
                                } : null;
                                setFormData(prev => ({
                                    ...prev,
                                    state: value
                                }));
                            }}
                            isDisabled={
                                createChannelMutation.isPending ||
                                updateChannelMutation.isPending ||
                                !safeFormData.country
                            }
                            countrySelected={!!safeFormData.country}
                        />
                        {errors.state && (
                            <div className="invalid-feedback d-block">
                                {errors.state}
                            </div>
                        )}
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col md="6">
                    <FormGroup>
                        <Label for='county' className='form-control-label'>
                            {t('channelsAdd.county')}
                        </Label>
                        <Input
                            id="county"
                            name="county"
                            type="text"
                            value={safeFormData.county}
                            onChange={handleInputChange}
                            className="form-control"
                            style={inputStyle}
                            placeholder={t('channelsAdd.enterCounty')}
                            disabled={createChannelMutation.isPending || updateChannelMutation.isPending}
                        />
                    </FormGroup>
                </Col>
                <Col md="6">
                    <FormGroup>
                        <Label for='country' className='form-control-label'>
                            {t('channelsAdd.country')} <span style={{ color: 'red' }}>*</span>
                        </Label>
                        <Select
                            activeColor={activeColor}
                            styles={selectStyles}
                            className="react-select"
                            name="country"
                            isSearchable
                            isClearable
                            closeMenuOnSelect
                            options={countryOptions}
                            placeholder={'Select country'}
                            value={mapValueToOption(safeFormData.country, countryOptions)}
                            onChange={handleSingleSelectChange('country')}
                            isDisabled={createChannelMutation.isPending || updateChannelMutation.isPending}
                        />
                        {errors.country && (
                            <div className="invalid-feedback d-block">
                                {errors.country}
                            </div>
                        )}
                    </FormGroup>
                </Col>
            </Row>

            <h5 className="mt-2">Contact Information</h5>
            {/* Contact Information */}
            <Row>
                <Col md="4">
                    <FormGroup>
                        <Label for='contact_person' className='form-control-label'>
                            {t('channelsAdd.contactPerson')}
                        </Label>
                        <Input
                            id="contact_person"
                            name="contact_person"
                            type="text"
                            value={safeFormData.contact_person}
                            onChange={handleInputChange}
                            className="form-control"
                            style={inputStyle}
                            placeholder={t('channelsAdd.enterContactPersonName')}
                            disabled={createChannelMutation.isPending || updateChannelMutation.isPending}
                        />
                    </FormGroup>
                </Col>
                <Col md="4">
                    <FormGroup>
                        <Label for='phone_1' className='form-control-label'>
                            {t('channelsAdd.phone1')} <span style={{ color: 'red' }}>*</span>
                        </Label>
                        <Input
                            id="phone_1"
                            name="phone_1"
                            type="text"
                            value={safeFormData.phone_1}
                            onChange={handleInputChange}
                            className={inputClass(errors.phone_1 && "is-invalid")}
                            style={inputStyle}
                            placeholder={t('channelsAdd.enterPhoneNumber')}
                            disabled={createChannelMutation.isPending || updateChannelMutation.isPending}
                        />
                        {errors.phone_1 && (
                            <div className="invalid-feedback d-block">
                                {errors.phone_1}
                            </div>
                        )}
                    </FormGroup>
                </Col>
                <Col md="4">
                    <FormGroup>
                        <Label for='phone_2' className='form-control-label'>
                            {t('channelsAdd.phone2')}
                        </Label>
                        <Input
                            id="phone_2"
                            name="phone_2"
                            type="text"
                            value={safeFormData.phone_2}
                            onChange={handleInputChange}
                            className="form-control"
                            style={inputStyle}
                            placeholder={t('channelsAdd.enterPhoneNumber')}
                            disabled={createChannelMutation.isPending || updateChannelMutation.isPending}
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col md="4">
                    <FormGroup>
                        <Label for='fax_1' className='form-control-label'>
                            {t('channelsAdd.fax1')}
                        </Label>
                        <Input
                            id="fax_1"
                            name="fax_1"
                            type="text"
                            value={safeFormData.fax_1}
                            onChange={handleInputChange}
                            className="form-control"
                            style={inputStyle}
                            placeholder={t('channelsAdd.enterFaxNumber')}
                            disabled={createChannelMutation.isPending || updateChannelMutation.isPending}
                        />
                    </FormGroup>
                </Col>
                <Col md="4">
                    <FormGroup>
                        <Label for='fax_2' className='form-control-label'>
                            {t('channelsAdd.fax2')}
                        </Label>
                        <Input
                            id="fax_2"
                            name="fax_2"
                            type="text"
                            value={safeFormData.fax_2}
                            onChange={handleInputChange}
                            className="form-control"
                            style={inputStyle}
                            placeholder={t('channelsAdd.enterFaxNumber')}
                            disabled={createChannelMutation.isPending || updateChannelMutation.isPending}
                        />
                    </FormGroup>
                </Col>
                <Col md="4">
                    <FormGroup>
                        <Label for='website' className='form-control-label'>
                            {t('channelsAdd.website')}
                        </Label>
                        <Input
                            id="website"
                            name="website"
                            type="url"
                            value={safeFormData.website}
                            onChange={handleInputChange}
                            className="form-control"
                            style={inputStyle}
                            placeholder={t('channelsAdd.enterWebsiteUrl')}
                            disabled={createChannelMutation.isPending || updateChannelMutation.isPending}
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col md="6">
                    <FormGroup>
                        <Label for='email' className='form-control-label'>
                            {t('channelsAdd.email')}
                        </Label>
                        <Input
                            id="email"
                            name="email"
                            type="email"
                            value={safeFormData.email}
                            onChange={handleInputChange}
                            className="form-control"
                            style={inputStyle}
                            placeholder={t('channelsAdd.enterEmailAddress')}
                            disabled={createChannelMutation.isPending || updateChannelMutation.isPending}
                        />
                    </FormGroup>
                </Col>
            </Row>

            <h5 className="mt-2">Additional Information</h5>
            {/* Additional Information */}
            <Row>
                <Col md="6">
                    <FormGroup>
                        <Label for='text_name' className='form-control-label'>
                            {t('channelsAdd.textName')} <small>(max 12 characters)</small>
                        </Label>
                        <Input
                            id="text_name"
                            name="text_name"
                            type="text"
                            value={safeFormData.text_name}
                            onChange={handleInputChange}
                            className="form-control"
                            style={inputStyle}
                            maxLength={12}
                            placeholder={t('channelsAdd.enterTextName')}
                            disabled={createChannelMutation.isPending || updateChannelMutation.isPending}
                        />
                    </FormGroup>
                </Col>
                <Col md="6">
                    <FormGroup>
                        <Label for='accounting_class' className='form-control-label'>
                            {t('channelsAdd.accountingClass')}
                        </Label>
                        <Input
                            id="accounting_class"
                            name="accounting_class"
                            type="text"
                            value={safeFormData.accounting_class}
                            onChange={handleInputChange}
                            className="form-control"
                            style={inputStyle}
                            placeholder={t('channelsAdd.enterAccountingClass')}
                            disabled={createChannelMutation.isPending || updateChannelMutation.isPending}
                        />
                    </FormGroup>
                </Col>
            </Row>
            {safeFormData.type === 'sales_group_person' && (
                <>
                    <Row>
                        <Col md="6">
                            <div className="">
                                <Row className="align-items-center">
                                    <Col md="4">
                                        <FormGroup check className="mb-0 d-flex align-items-center">
                                            <Label check className="mb-0 d-flex align-items-center">
                                                <Input
                                                    id="sales_role"
                                                    name="sales_role"
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={!!safeFormData.sales_role}
                                                    onChange={handleInputChange}
                                                    disabled={createChannelMutation.isPending || updateChannelMutation.isPending}
                                                />
                                                <span className="form-check-sign">
                                                    <span className="check" />
                                                </span>
                                                <span style={{ marginLeft: 8 }}>Sales Role</span>
                                            </Label>
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup check className="mb-0 d-flex align-items-center">
                                            <Label check className="mb-0 d-flex align-items-center">
                                                <Input
                                                    id="fi_role"
                                                    name="fi_role"
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={!!safeFormData.fi_role}
                                                    onChange={handleInputChange}
                                                    disabled={createChannelMutation.isPending || updateChannelMutation.isPending}
                                                />
                                                <span className="form-check-sign">
                                                    <span className="check" />
                                                </span>
                                                <span style={{ marginLeft: 8 }}>FI Role</span>
                                            </Label>
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup check className="mb-0 d-flex align-items-center">
                                            <Label check className="mb-0 d-flex align-items-center">
                                                <Input
                                                    id="sales_manager"
                                                    name="sales_manager"
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={!!safeFormData.sales_manager}
                                                    onChange={handleInputChange}
                                                    disabled={createChannelMutation.isPending || updateChannelMutation.isPending}
                                                />
                                                <span className="form-check-sign">
                                                    <span className="check" />
                                                </span>
                                                <span style={{ marginLeft: 8 }}>Manager Role</span>
                                            </Label>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="12" className="mt-2">
                                        <FormGroup>
                                            <Label className='form-control-label'>Employee Code:</Label>
                                            <Input
                                                id="employee_code"
                                                name="employee_code"
                                                type="text"
                                                value={safeFormData.employee_code}
                                                onChange={handleInputChange}
                                                placeholder={t('channelsAdd.enterEmployeeCode') || 'Enter Employee Code'}
                                                disabled={createChannelMutation.isPending || updateChannelMutation.isPending}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </div>
                        </Col>
                    </Row>
                </>
            )}
            {channelType === 'sales_location_branch' && (
                <>
                    <Row>
                        <Col md="3">
                            <FormGroup>
                                <Label for='dealer_number' className='form-control-label'>
                                    {t('channelsAdd.dealerNumber')}
                                </Label>
                                <Input
                                    id="dealer_number"
                                    name="dealer_number"
                                    type="text"
                                    value={safeFormData.dealer_number}
                                    onChange={handleInputChange}
                                    placeholder={t('channelsAdd.enterDealerNumber')}
                                    disabled={createChannelMutation.isPending || updateChannelMutation.isPending}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label className='form-control-label'>
                                    {t('channelsAdd.assignedBookkeepingCompany')}
                                </Label>
                                <Select
                                    activeColor={activeColor}
                                    styles={selectStyles}
                                    className="react-select"
                                    placeholder={t('channelsAdd.selectBookkeepingCompany')}
                                    value={bookkeepingCompanyOptions.find(opt => opt.value === safeFormData.bookkeeping_company) || null}
                                    onChange={(option) => setFormData(prev => ({ ...prev, bookkeeping_company: option?.value || '' }))}
                                    options={bookkeepingCompanyOptions}
                                    isClearable
                                    isDisabled={createChannelMutation.isPending || updateChannelMutation.isPending}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className='form-control-label'>
                                    {t('channelsAdd.locationCode')}
                                </Label>
                                <Select
                                    activeColor={activeColor}
                                    styles={selectStyles}
                                    className="react-select"
                                    placeholder={t('channelsAdd.selectLocationCode')}
                                    value={locationOptions.find(opt => opt.value === safeFormData.location_code) || null}
                                    onChange={(option) => setFormData(prev => ({ ...prev, location_code: option?.value || '' }))}
                                    options={locationOptions}
                                    isClearable
                                    isDisabled={createChannelMutation.isPending || updateChannelMutation.isPending}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </>
            )}
        </div>
    );
});

ChannelDetailForm.displayName = 'ChannelDetailForm';

export default ChannelDetailForm;
